import React from "react";
import { motion } from "framer-motion";
import { ProblemSectionProps } from "@/types/components";

const DEFAULT_PROBLEMS = [
  {
    title: "Inefficient Manual Processes",
    subtitle: "Hindering Progress",
    description: "Time-consuming manual data entry, document processing, and business processes slow down operations and increase errors."
  },
  {
    title: "Silos and Isolated Tech Stacks",
    subtitle: "Fragmented Systems",
    description: "Disconnected systems and data silos prevent seamless information flow and decision-making."
  },
  {
    title: "Poor User Experience",
    subtitle: "Staff, Agents, Stakeholders",
    description: "Complex interfaces and workflows frustrate users and reduce productivity."
  },
  {
    title: "Limited Real-Time Insights",
    subtitle: "Lack of Data Visibility",
    description: "Delayed reporting and analytics hinder quick decision-making and risk assessment."
  }
];

const ProblemSection: React.FC<ProblemSectionProps> = ({ problems = DEFAULT_PROBLEMS, className }) => {
  return (
    <section className="relative py-20 bg-gradient-to-b from-gray-900 to-gray-800">
      {/* Background overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(120,119,198,0.1),rgba(255,255,255,0))]" />
      
      <div className={`container mx-auto px-4 relative z-10 ${className || ''}`}>
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-emerald-400">
            Problem
          </h2>
          <p className="text-2xl text-gray-400">
            State of Current Workflow
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {problems.map((problem, index) => (
            <motion.div
              key={problem.title}
              className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <h3 className="text-2xl md:text-3xl font-semibold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-emerald-400">
                {problem.title}
              </h3>
              <p className="text-lg text-gray-500 mb-3">
                {problem.subtitle}
              </p>
              <p className="text-xl text-gray-400">
                {problem.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;
